import { useEffect, useState } from 'react';
import { adminApi } from '../../utils/api';
import { Alert, Badge, Button, Card, Input, Modal, Spinner, Toggle } from './shared';

const EMPTY = { name: '', expires_in_days: 30 };

function formatDate(value) {
    if (!value) return '—';
    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
}

function keyStatus(row) {
    if (row.revoked_at) return { label: 'Revoked', variant: 'danger' };
    if (row.expires_at && new Date(row.expires_at) < new Date()) {
        return { label: 'Expired', variant: 'warning' };
    }
    if (!row.is_active) return { label: 'Disabled', variant: 'warning' };
    return { label: 'Active', variant: 'success' };
}

export default function AdminApiKeys() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [formOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [creating, setCreating] = useState(false);

    const [revealed, setRevealed] = useState(null); // { id, name, key_prefix, key }

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const res = await adminApi.listApiKeys();
            setRows(res?.items || []);
            setError(null);
        } catch (e) {
            setError(e.message || 'Failed to load API keys');
        } finally {
            setLoading(false);
        }
    };

    const openCreate = () => {
        setForm({ ...EMPTY });
        setFormOpen(true);
    };

    const handleCreate = async () => {
        if (!form.name.trim()) {
            setError('Name is required');
            return;
        }
        setCreating(true);
        try {
            const payload = { name: form.name.trim() };
            if (form.expires_in_days) {
                payload.expires_in_days = Number(form.expires_in_days);
            }
            const created = await adminApi.createApiKey(payload);
            setFormOpen(false);
            setRevealed(created);
            setSuccess(`Created key "${created.name}" — copy the raw key below, it won't be shown again.`);
            await load();
        } catch (e) {
            setError(e.message || 'Failed to create key');
        } finally {
            setCreating(false);
        }
    };

    const handleToggle = async (row) => {
        try {
            await adminApi.updateApiKey(row.id, { is_active: !row.is_active });
            setSuccess(`Updated "${row.name}"`);
            await load();
        } catch (e) {
            setError(e.message || 'Failed to update');
        }
    };

    const handleRevoke = async (row) => {
        if (!confirm(`Revoke "${row.name}" (${row.key_prefix}…)? This cannot be undone.`)) return;
        try {
            await adminApi.revokeApiKey(row.id);
            setSuccess(`Revoked "${row.name}"`);
            await load();
        } catch (e) {
            setError(e.message || 'Failed to revoke');
        }
    };

    const handleCopy = (text) => {
        navigator.clipboard
            .writeText(text)
            .then(() => setSuccess('Copied to clipboard'))
            .catch(() => setError('Copy failed — hãy chọn và Ctrl+C thủ công'));
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">API Keys</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        Personal API keys (<code>sk_live_…</code>) — dùng cho bot, CI, scheduled job. Mỗi user thấy và quản lý key của chính mình.
                        Key chỉ hiện raw 1 lần khi tạo — lưu ngay sau đó.
                    </p>
                </div>
                <Button onClick={openCreate}>+ New API key</Button>
            </div>

            {error && (
                <Alert variant="danger" onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {loading ? (
                <div className="flex justify-center py-10">
                    <Spinner />
                </div>
            ) : rows.length === 0 ? (
                <Card className="p-8 text-center text-sm text-gray-500">
                    Chưa có API key nào. Nhấn <strong>+ New API key</strong> để tạo key đầu tiên.
                </Card>
            ) : (
                <Card>
                    <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                            <tr>
                                <th className="px-4 py-3">Name</th>
                                <th className="px-4 py-3">Prefix</th>
                                <th className="px-4 py-3">Status</th>
                                <th className="px-4 py-3">Created</th>
                                <th className="px-4 py-3">Last used</th>
                                <th className="px-4 py-3">Expires</th>
                                <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map((row) => {
                                const status = keyStatus(row);
                                return (
                                    <tr key={row.id} className="hover:bg-gray-50">
                                        <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                                        <td className="px-4 py-3 font-mono text-xs text-gray-600">{row.key_prefix}…</td>
                                        <td className="px-4 py-3">
                                            <Badge variant={status.variant}>{status.label}</Badge>
                                        </td>
                                        <td className="px-4 py-3 text-gray-600">{formatDate(row.created_at)}</td>
                                        <td className="px-4 py-3 text-gray-600">{formatDate(row.last_used_at)}</td>
                                        <td className="px-4 py-3 text-gray-600">{formatDate(row.expires_at)}</td>
                                        <td className="px-4 py-3 text-right space-x-2">
                                            {!row.revoked_at && (
                                                <Toggle
                                                    checked={row.is_active}
                                                    onChange={() => handleToggle(row)}
                                                    label={row.is_active ? 'On' : 'Off'}
                                                />
                                            )}
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleRevoke(row)}
                                                disabled={!!row.revoked_at}
                                            >
                                                Revoke
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </Card>
            )}

            {/* Create modal */}
            <Modal
                open={formOpen}
                onClose={() => setFormOpen(false)}
                size="md"
                title="New API key"
            >
                <Modal.Body>
                    <Input
                        label="Name *"
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                        placeholder="CI bot"
                    />
                    <Input
                        label="Expires in (days)"
                        hint="1–3650, khuyên 30–90 ngày"
                        type="number"
                        min={1}
                        max={3650}
                        value={form.expires_in_days}
                        onChange={(e) => setForm((f) => ({ ...f, expires_in_days: e.target.value }))}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={creating}>
                        Cancel
                    </Button>
                    <Button onClick={handleCreate} disabled={creating}>
                        {creating ? 'Creating…' : 'Create'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Reveal raw key modal — show only once */}
            <Modal
                open={!!revealed}
                onClose={() => setRevealed(null)}
                size="md"
                title="API key created"
            >
                <Modal.Body>
                    {revealed && (
                        <>
                            <Alert variant="warning">
                                Raw key chỉ hiển thị <strong>1 lần duy nhất</strong>. Lưu ngay — nếu mất phải tạo key mới.
                            </Alert>
                            <div>
                                <div className="text-xs font-medium text-gray-500 mb-1">Name</div>
                                <div className="text-sm text-gray-900">{revealed.name}</div>
                            </div>
                            <div>
                                <div className="text-xs font-medium text-gray-500 mb-1">Key</div>
                                <div className="flex items-start gap-2">
                                    <code className="flex-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs font-mono text-gray-900 break-all">
                                        {revealed.key}
                                    </code>
                                    <Button variant="ghost" onClick={() => handleCopy(revealed.key)}>
                                        Copy
                                    </Button>
                                </div>
                            </div>
                            <div className="text-xs text-gray-500">
                                Prefix: <code className="font-mono">{revealed.key_prefix}</code> · Expires: {formatDate(revealed.expires_at)}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button onClick={() => setRevealed(null)}>Done</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
